import Link from 'next/link';
import { useRouter } from 'next/router';
import { memo, useEffect, useRef, useState } from 'react';
import style from './topics-block.module.scss';
import { BLOG_TOPICS, DEFAULT_BLOG_TOPIC } from '@models/blog.topics';
import arrowRight from '/public/assets/icons/arrow-right.svg'
import arrowLeft from '/public/assets/icons/arrow-left.svg'

import Image from 'next/image';
import { restoreScrollBehavior, setScrollBehaviorAuto } from '@helpers/smooth-scroll.helper';

const TopicsBlock = ({ onTopicChange }: any): JSX.Element => {
    let [topic, setTopic] = useState<string>(DEFAULT_BLOG_TOPIC.slug);
    let [FirstTopicVisible, setIsFirstTopicVisible] = useState(false);
    let [LastTopicVisible, setIsLastTopicVisible] = useState(false);
    const [hasTopicEffectRun, setHasTopicEffectRun] = useState(false);
    const [isChangeFromButton, setIsChangeFromButton] = useState(false);

    useEffect(() => {
        setScrollBehaviorAuto();
        return () => {
            restoreScrollBehavior();
        };
    }, []);

    let router = useRouter();
    const scrollContainerRef = useRef<(HTMLDivElement | null)>(null);
    const AMOUNT_SCROLL = 500;
    const buttonRefs = useRef<Array<HTMLLIElement | null>>([]);
    const thresholdObserver = 0.5; 

    useEffect(() => {
        let currentTopic: string = DEFAULT_BLOG_TOPIC.slug;

         if (
            typeof router.query.topic === 'string' &&
            BLOG_TOPICS.some((topic) => topic.slug === router.query.topic)
        ) {
            currentTopic = router.query.topic;
        }

        setTopic(currentTopic);

        if (typeof onTopicChange === 'function') {
            onTopicChange(currentTopic);
        }
        setHasTopicEffectRun(true);

    }, [router.query.topic]);

    useEffect(() => {
        if (!isChangeFromButton) {
            const topicIndex = buttonRefs.current.findIndex(buttonElement => buttonElement?.textContent === topic);

            if (topicIndex !== -1) {
                scrollContainerRef.current?.scrollTo({ left: 0, behavior: "auto" });
                const buttonWidth = buttonRefs.current[topicIndex]!.offsetWidth;
                const buttonLeft = buttonRefs.current[topicIndex]!.getBoundingClientRect().left;
                const containerLeft = scrollContainerRef.current?.getBoundingClientRect().left;
                const scrollLeft = buttonLeft - containerLeft! - buttonWidth;
                const scrollTo = scrollLeft - (scrollContainerRef.current?.scrollLeft || 0);
                scrollContainerRef.current?.scrollTo({ left: scrollTo, behavior: "smooth" });
            }
        } else {
            setIsChangeFromButton(false);
        }
    }, [hasTopicEffectRun, topic])

    
    useEffect(() => {
            let scrollVisible:boolean = false;
            const firstButtonElement = buttonRefs.current[0];
            const firstObserver = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setIsFirstTopicVisible(false);
                    } else if (!entry.isIntersecting && scrollVisible){
                        setIsFirstTopicVisible(true);
                    }
                });
            }, { threshold: thresholdObserver});

            const lastButtonElement = buttonRefs.current[buttonRefs.current.length - 1];
            const lastObserver = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setIsLastTopicVisible(false);
                    } else {
                        setIsLastTopicVisible(true);
                    }
                });
            }, { threshold: thresholdObserver });


            const scrollContainerObserver = scrollContainerRef.current;
            const scrollObserver = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        scrollVisible = true;                        
                    } else {
                        scrollVisible = false;                        
                    }
                });
            }, { threshold: 1 });

            if (scrollContainerObserver) {
                scrollObserver.observe(scrollContainerObserver);
            }

            if (firstButtonElement) {
                firstObserver.observe(firstButtonElement);
            }

            if (lastButtonElement) {
                lastObserver.observe(lastButtonElement);
            }
    }, []);

    const scrollButtonIntoView = (buttonIndex: number) => {
        const buttonElement = buttonRefs.current[buttonIndex];
        setIsChangeFromButton(true);
        if (buttonElement) {
            const containerWidth = scrollContainerRef.current?.getBoundingClientRect().width ?? 0;
            const buttonWidth = buttonElement.offsetWidth;
            const buttonLeft = buttonElement.getBoundingClientRect().left;
            const containerLeft = scrollContainerRef.current?.getBoundingClientRect().left ?? 0;
            const distanceFromContainerStart = buttonLeft - containerLeft;
            const isFullyVisible = distanceFromContainerStart >= 0 && (distanceFromContainerStart + buttonWidth) <= containerWidth;

            if (!isFullyVisible) {
                const isNearEnd = distanceFromContainerStart + buttonWidth > containerWidth / 2;
                const scrollDistance = isNearEnd ? distanceFromContainerStart - containerWidth + buttonWidth : distanceFromContainerStart;
                scrollContainerRef.current?.scrollBy({ left: scrollDistance, behavior: 'smooth' });
            }
        }
    };

    const changedisplayedTopicsLeft = () => {
        setIsFirstTopicVisible(false);
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({
                left: -AMOUNT_SCROLL,
                behavior: 'smooth'
            });
        }
    };

    const changedisplayedTopicsRight = () => {
        setIsLastTopicVisible(false);
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({
                left: +AMOUNT_SCROLL,
                behavior: 'smooth'
            });
        }
    };
    
    const setButtonRef = (el: HTMLLIElement | null, index:number) => {
        buttonRefs.current[index] = el;
    };

    const buildQueryParams = (item: string): string => {
        return item !== DEFAULT_BLOG_TOPIC.slug ? `?topic=${item}` : '';
    }

    return (
        <>
            <div className={style.topics__wrapper}>
                {FirstTopicVisible && (
                    <div className={style.topics__wrapper__arrow__icon}>
                        <Image onClick={changedisplayedTopicsLeft} alt="arrow-icon" src={arrowLeft} />
                    </div>
                )}
                <div className={style.topics__wrapper__scroll__container} ref={scrollContainerRef}>
                    <div className={style.topics__wrapper__scroll__container__list}>
                        {
                            BLOG_TOPICS.map((item, index) => {
                                return (
                                    <li key={item.slug} onClick={() => scrollButtonIntoView(index)}
                                        className={item.slug === topic ? `${style.topics__item} ${style.topics__item_active}` : style.topics__item} ref={el => setButtonRef(el, index)}>
                                        <Link scroll={false} href={`${router.basePath}${buildQueryParams(item.slug)}`} shallow={true}>
                                            <a style={{ textTransform: 'capitalize' }}>{item.title}</a>
                                        </Link>
                                    </li>
                                );
                            })
                        }
                    </div>
                </div>
                {LastTopicVisible &&(
                    <div className={style.topics__wrapper__arrow__icon}>
                        <Image onClick={changedisplayedTopicsRight} alt="arrow-icon" src={arrowRight} />
                    </div>
                )}
            </div>
        </>
    )
}

export default memo(TopicsBlock);
